/**
 * TrainLoopSection: the whole train loop for this team (Settings >
 * Pipeline). The diagram shows the ten stages and which ones run, and
 * each node's hover says what the stage does. The table under it is one
 * line per stage, where it stands for the team and where to change
 * that, with the stage's jobs under it (StageTable).
 *
 * The stages write nothing: the states come from the rows the jobs read,
 * and each stage's own page is where they change. So the page reads the
 * same under staff impersonation as it does for the team, and the stage
 * links hide with the sidebar pages they depend on (TRA-927).
 */

import { getApiErrorDetail } from '@/api/client'
import type { TrainLoopStatus } from '@/api/generated/model'
import type { SettingsSectionProps } from '@/components/settings/sections'
import { SettingsPageHeader } from '@/components/settings/SettingsPageHeader'
import { SettingsBlock } from '@/components/settings/SettingsSection'
import { Skeleton } from '@/components/ui/skeleton'
import { useTrainLoopLinks, useTrainLoopStatus } from '@/hooks/useTeamSettings'

import { StageTable } from './StageTable'
import { TrainLoopDiagram } from './TrainLoopDiagram'

export function TrainLoopSection({ section }: SettingsSectionProps): React.JSX.Element {
  const loopQuery = useTrainLoopStatus()

  return (
    <div>
      <SettingsPageHeader section={section} />

      {loopQuery.isLoading ? (
        <SettingsBlock>
          <Skeleton className="h-64 w-full" />
        </SettingsBlock>
      ) : loopQuery.data === undefined ? (
        <SettingsBlock>
          <p className="text-sm text-destructive" role="alert">
            {getApiErrorDetail(loopQuery.error) ?? 'The pipeline could not be loaded.'}
          </p>
        </SettingsBlock>
      ) : (
        <LoadedLoop status={loopQuery.data} />
      )}
    </div>
  )
}

function LoadedLoop({ status }: { status: TrainLoopStatus }): React.JSX.Element {
  const linksQuery = useTrainLoopLinks()
  return (
    <>
      <SettingsBlock>
        <TrainLoopDiagram status={status} />
        <p className="mt-4 text-xs text-muted-foreground">
          Pointed boxes need a person to approve. Dashed boxes are not built yet. Hover a box
          to see what it does.
        </p>
      </SettingsBlock>
      <SettingsBlock>
        <div className="mb-4 space-y-1">
          <h2 className="font-serif text-base font-medium text-foreground">Stages</h2>
          <p className="text-xs text-muted-foreground">
            Status, last run and next start for each stage.
          </p>
        </div>
        {linksQuery.isLoading ? (
          <Skeleton className="mb-4 h-8 w-full" />
        ) : linksQuery.isError ? (
          <p className="mb-4 text-sm text-destructive" role="alert">
            {getApiErrorDetail(linksQuery.error) ?? 'The jobs could not be loaded.'}
          </p>
        ) : null}
        <StageTable status={status} links={linksQuery.data?.links ?? []} />
      </SettingsBlock>
    </>
  )
}
